import { useEffect, useRef, useState } from 'react';
import {
  Box, Stack, Select, MenuItem, Checkbox, FormControlLabel, TextField,
  Typography, RadioGroup, Radio,
} from '@mui/material';
import { RRule, Frequency } from 'rrule';
import { useTranslation } from 'react-i18next';

export interface RRuleBuilderProps {
  value: string;                       // RRULE 字串（可空，不含 "RRULE:" 前綴）
  onChange: (rrule: string) => void;   // 變動時組好的 RRULE 字串
}

type Mode = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
type EndsMode = 'never' | 'on_date' | 'after_n';

const FREQ_MAP: Record<string, Frequency> = {
  daily: Frequency.DAILY,
  weekly: Frequency.WEEKLY,
  monthly: Frequency.MONTHLY,
  yearly: Frequency.YEARLY,
};
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export default function RRuleBuilder({ value, onChange }: RRuleBuilderProps) {
  const { t } = useTranslation();
  const [mode, setMode]             = useState<Mode>('none');
  const [interval, setIntervalN]    = useState<number>(1);
  const [byweekday, setByweekday]   = useState<number[]>([]);   // 0=MO ~ 6=SU
  const [bymonthday, setBymonthday] = useState<number>(1);
  const [endsMode, setEndsMode]     = useState<EndsMode>('never');
  const [untilDate, setUntilDate]   = useState<string>('');
  const [countN, setCountN]         = useState<number>(10);
  const [custom, setCustom]         = useState<string>('');
  const loaded = useRef(false);

  // Parse 既有 RRULE 載入 state（只跑一次）
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    if (!value) { setMode('none'); return; }
    try {
      const rule = RRule.fromString(value.startsWith('RRULE:') ? value : `RRULE:${value}`);
      const o = rule.origOptions;
      switch (o.freq) {
        case Frequency.DAILY:   setMode('daily');   break;
        case Frequency.WEEKLY:  setMode('weekly');  break;
        case Frequency.MONTHLY: setMode('monthly'); break;
        case Frequency.YEARLY:  setMode('yearly');  break;
        default: setMode('custom'); setCustom(value); return;
      }
      if (o.interval) setIntervalN(o.interval);
      if (o.byweekday) {
        const arr = Array.isArray(o.byweekday) ? o.byweekday : [o.byweekday];
        setByweekday(arr.map((w: unknown) =>
          typeof w === 'number' ? w : (w as { weekday: number }).weekday));
      }
      if (o.bymonthday) {
        setBymonthday(Array.isArray(o.bymonthday) ? o.bymonthday[0] : o.bymonthday);
      }
      if (o.until) { setEndsMode('on_date'); setUntilDate(new Date(o.until).toISOString().slice(0, 10)); }
      else if (o.count) { setEndsMode('after_n'); setCountN(o.count); }
      else setEndsMode('never');
    } catch {
      setMode('custom'); setCustom(value);
    }
  }, [value]);

  // 組 RRULE 字串
  useEffect(() => {
    if (!loaded.current) return;
    if (mode === 'none')   { onChange(''); return; }
    if (mode === 'custom') { onChange(custom); return; }
    const opts: Partial<RRule['origOptions']> = { freq: FREQ_MAP[mode] };
    if (interval > 1) opts.interval = interval;
    if (mode === 'weekly' && byweekday.length > 0) opts.byweekday = byweekday;
    if (mode === 'monthly') opts.bymonthday = [bymonthday];
    if (endsMode === 'on_date' && untilDate) opts.until = new Date(untilDate);
    if (endsMode === 'after_n' && countN > 0) opts.count = countN;
    onChange(new RRule(opts).toString().replace(/^RRULE:/, ''));
  }, [mode, interval, byweekday, bymonthday, endsMode, untilDate, countN, custom]); // eslint-disable-line

  // 人話描述（rrule.js toText）
  const summary = (() => {
    if (mode === 'none' || mode === 'custom') return '';
    try {
      const opts: Partial<RRule['origOptions']> = { freq: FREQ_MAP[mode] };
      if (interval > 1) opts.interval = interval;
      if (mode === 'weekly' && byweekday.length > 0) opts.byweekday = byweekday;
      if (mode === 'monthly') opts.bymonthday = [bymonthday];
      if (endsMode === 'on_date' && untilDate) opts.until = new Date(untilDate);
      if (endsMode === 'after_n' && countN > 0) opts.count = countN;
      return new RRule(opts).toText();
    } catch { return ''; }
  })();

  return (
    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('calendar.rrule', '循環設定')}
      </Typography>

      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{t('myCalendar.rruleEvery', '每')}</Typography>
          {mode !== 'none' && mode !== 'custom' && (
            <TextField
              type="number" size="small" value={interval}
              onChange={e => setIntervalN(Math.max(1, parseInt(e.target.value) || 1))}
              sx={{ width: 70 }} inputProps={{ min: 1 }}
            />
          )}
          <Select size="small" value={mode}
                  onChange={e => setMode(e.target.value as Mode)} sx={{ minWidth: 110 }}>
            <MenuItem value="none">{t('myCalendar.rruleNever', '不循環')}</MenuItem>
            <MenuItem value="daily">{t('myCalendar.rruleDaily', '天')}</MenuItem>
            <MenuItem value="weekly">{t('myCalendar.rruleWeekly', '週')}</MenuItem>
            <MenuItem value="monthly">{t('myCalendar.rruleMonthly', '月')}</MenuItem>
            <MenuItem value="yearly">{t('myCalendar.rruleYearly', '年')}</MenuItem>
            <MenuItem value="custom">{t('myCalendar.rruleCustom', '自訂')}</MenuItem>
          </Select>
        </Stack>

        {/* 每週 — 選星期 */}
        {mode === 'weekly' && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {WEEKDAYS.map((d, i) => (
              <FormControlLabel
                key={d}
                control={
                  <Checkbox size="small" checked={byweekday.includes(i)}
                    onChange={e => {
                      if (e.target.checked) setByweekday([...byweekday, i].sort((a, b) => a - b));
                      else setByweekday(byweekday.filter(x => x !== i));
                    }} />
                }
                label={<Typography variant="caption">{t(`myCalendar.weekday.${d}`, d)}</Typography>}
                sx={{ mr: 0.5 }}
              />
            ))}
          </Stack>
        )}

        {/* 每月 — 選日 */}
        {mode === 'monthly' && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">{t('myCalendar.rruleMonthDay', '每月第')}</Typography>
            <TextField
              type="number" size="small" value={bymonthday}
              onChange={e => setBymonthday(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
              sx={{ width: 70 }} inputProps={{ min: 1, max: 31 }}
            />
            <Typography variant="body2">{t('myCalendar.rruleMonthDaySuffix', '天')}</Typography>
          </Stack>
        )}

        {/* 自訂 — 手動 RRULE 字串 */}
        {mode === 'custom' && (
          <TextField
            label="RRULE" size="small" value={custom}
            onChange={e => setCustom(e.target.value)}
            placeholder="FREQ=WEEKLY;BYDAY=MO,WE,FR" fullWidth
          />
        )}

        {/* 結束於 */}
        {mode !== 'none' && mode !== 'custom' && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {t('myCalendar.rruleEndsAt', '結束於')}
            </Typography>
            <RadioGroup row value={endsMode} onChange={e => setEndsMode(e.target.value as EndsMode)}>
              <FormControlLabel value="never" control={<Radio size="small" />}
                                label={<Typography variant="body2">{t('myCalendar.rruleEndsNever', '永不')}</Typography>} />
              <FormControlLabel value="on_date" control={<Radio size="small" />}
                                label={<Typography variant="body2">{t('myCalendar.rruleEndsOnDate', '指定日期')}</Typography>} />
              <FormControlLabel value="after_n" control={<Radio size="small" />}
                                label={<Typography variant="body2">{t('myCalendar.rruleEndsAfterN', '循環次數')}</Typography>} />
            </RadioGroup>
            {endsMode === 'on_date' && (
              <TextField type="date" size="small" value={untilDate}
                         onChange={e => setUntilDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            )}
            {endsMode === 'after_n' && (
              <TextField type="number" size="small" value={countN}
                         onChange={e => setCountN(Math.max(1, parseInt(e.target.value) || 1))}
                         sx={{ width: 100 }} inputProps={{ min: 1 }} />
            )}
          </Box>
        )}

        {/* 人話描述 */}
        {summary && (
          <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('myCalendar.rruleSummary', '預覽')}：
            </Typography>
            <Typography variant="body2">{summary}</Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
